import {SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';
import {Platform} from 'react-native';
import {useAppDispatch} from '../../../redux';
import {
  changeCardApplicationDeliveryToIssuePoint,
  changeCardApplicationDeliveryToRussianPost,
} from '../../../redux/actions/login';
import {getHolderProfile} from '../../../redux/actions/profile';
import {addNotification} from '../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../shared/components/snackbar/model';
import {ServerValidationError} from '../../../shared/models/serverValidationError';
import {GetHolderProfileCabinetResponsePayload} from '../../../shared/models/api/getHolderProfileCabinetResponsePayload';
import {AuthorizeEsiaResponsePayload} from '../../../shared/models/api/authorizeEsiaResponsePayload';
import {CounterpartyLinkedProfile} from '../../../shared/hooks/useAccountLinkerHook/Types';
import {GetIssuePointCabinetResponsePayload} from '../../../shared/models/api/getIssuePointCabinetResponsePayload';
import {initialYandexZoomValue, NativeMapRegion} from '../esiaRegCard/components/map/models';
import {IssuePointsMapState} from '../esiaRegCard/components/map/useMap';
import {CardApplicationStatus} from '../../../types/enums/cardApplicationStatus';
import {DeliveryType} from '../../../types/registerCardApplicationRequestPayload';
import {RegExps} from '../../../shared/constants/regexps';

type EsiaRegInfo = CounterpartyLinkedProfile | AuthorizeEsiaResponsePayload;

const latOffset = 0.007;

const getAddressText = (value: unknown): string => {
  if (typeof value === 'string') return value;

  if (value && typeof value === 'object') {
    const targetValue = (value as {target?: {value?: unknown}}).target?.value;
    if (typeof targetValue === 'string') return targetValue;

    const directValue = (value as {value?: unknown}).value;
    if (typeof directValue === 'string') return directValue;
  }

  return '';
};

export const hasTrackingNumber = (
  profile: GetHolderProfileCabinetResponsePayload | null,
  esiaRegInfo?: EsiaRegInfo | null,
): boolean => {
  if (profile?.prerequestedCardApplicationHasTrackingNumber) return true;

  const trackingNumber = esiaRegInfo?.cardDeliveryInfo?.trackingNumber;
  return trackingNumber != null && trackingNumber !== '';
};

type Params = {
  activationIsUnavailable: boolean;
  isOwnAccount: boolean;
  cardApplicationId?: number;
  profile: GetHolderProfileCabinetResponsePayload | null;
  esiaRegInfo?: EsiaRegInfo | null;
  issuePointsMap: IssuePointsMapState;
};

export function useDeliveryMethodChange({
  isOwnAccount,
  cardApplicationId,
  profile,
  esiaRegInfo,
  issuePointsMap,
}: Params) {
  const dispatch = useAppDispatch();
  const {loading: mapLoading, mapData, onPressMarker, currentMarker, centerPoint, markers} = issuePointsMap;
  const [isIssuePointSelectionVisible, setIsIssuePointSelectionVisible] = useState(false);
  const [isIssuePointDetailsVisible, setIsIssuePointDetailsVisible] = useState(true);
  const [mapZoomLevel, setMapZoomLevel] = useState(initialYandexZoomValue);
  const [mapCenterPoint, setMapCenterPoint] = useState<[number, number]>([0, 0]);
  const [lastMarkerClickTimestamp, setLastMarkerClickTimestamp] = useState(Date.now());
  const [nativeRegion, setNativeRegion] = useState<NativeMapRegion>();
  const [deliveryChangeSubmitting, setDeliveryChangeSubmitting] = useState(false);
  const [destroyNativeMap, setDestroyNativeMap] = useState(Platform.OS === 'ios');
  const [isRussianPostAddressInputVisible, setIsRussianPostAddressInputVisible] = useState(false);
  const [russianPostDeliveryAddress, setRussianPostDeliveryAddressState] = useState(
    esiaRegInfo?.cardDeliveryInfo?.desiredAddress ?? '',
  );
  const setRussianPostDeliveryAddress = useCallback((value: SetStateAction<string> | unknown) => {
    if (typeof value === 'function') {
      setRussianPostDeliveryAddressState((prev) => getAddressText((value as (prev: string) => unknown)(prev)));
      return;
    }

    setRussianPostDeliveryAddressState(getAddressText(value));
  }, []);

  const resetDeliveryChangeSelection = useCallback(() => {
    setRussianPostDeliveryAddressState('');
    onPressMarker(null);
    setIsIssuePointDetailsVisible(true);
    setMapZoomLevel(initialYandexZoomValue);
    if (mapData.length) {
      setMapCenterPoint(centerPoint);
    }
    if (Platform.OS !== 'web') {
      setNativeRegion({lat: 56.976006, lon: 60.555874, zoom: 9});
    }
  }, [onPressMarker, mapData.length, centerPoint]);

  const currentDeliveryTypeId =
    profile?.prerequestedCardApplicationDeliveryTypeId ?? esiaRegInfo?.cardDeliveryInfo?.deliveryTypeId;
  const russianPostDeliveryAddressText = russianPostDeliveryAddress.trim();
  const isRussianPostDeliveryAddressValid =
    !russianPostDeliveryAddress.trim() || RegExps.regionRestriction.test(russianPostDeliveryAddress);

  const canChangeDeliveryMethod = useMemo(() => {
    if (!isOwnAccount || cardApplicationId == null) {
      return false;
    }

    // Russian Post: change delivery only while tracking number is not assigned
    if (currentDeliveryTypeId === DeliveryType.RussianPost) {
      return (
        !hasTrackingNumber(profile, esiaRegInfo) &&
        esiaRegInfo?.cardApplicationStatusId === CardApplicationStatus.AcceptFromClient
      );
    }

    // Issue point: change delivery only while application is not yet processed by an operator
    if (currentDeliveryTypeId === DeliveryType.IssuePoint) {
      return esiaRegInfo?.cardApplicationStatusId === CardApplicationStatus.AcceptFromClient;
    }

    return false;
  }, [isOwnAccount, cardApplicationId, currentDeliveryTypeId, profile, esiaRegInfo]);

  useEffect(() => {
    if (Platform.OS === 'ios' && isIssuePointSelectionVisible) {
      setTimeout(() => setDestroyNativeMap(false), 10);
    }
  }, [isIssuePointSelectionVisible]);

  useEffect(() => {
    if (currentMarker == null && mapData.length) {
      setMapZoomLevel(initialYandexZoomValue);
      setMapCenterPoint(centerPoint);
      if (Platform.OS !== 'web') {
        setNativeRegion({lat: 56.976006, lon: 60.555874, zoom: 9});
      }
    }
  }, [mapData, centerPoint, currentMarker]);

  const onIssuePointSelected = (issuePoint: GetIssuePointCabinetResponsePayload | null) => {
    if (!issuePoint) return;
    onPressMarker(issuePoint);
    setIsIssuePointDetailsVisible(true);
    if (issuePoint.geolocation?.length === 2) {
      setMapZoomLevel(14);
      setMapCenterPoint([issuePoint.geolocation[0] + latOffset, issuePoint.geolocation[1]]);
      if (Platform.OS !== 'web') {
        setNativeRegion({
          lat: issuePoint.geolocation[0] + latOffset,
          lon: issuePoint.geolocation[1],
          zoom: 14,
        });
      }
    }
  };

  const onNativeIssuePointSelected = (issuePointId: number) => {
    const issuePoint = mapData.find((point) => point.id === issuePointId);
    if (issuePoint) onIssuePointSelected(issuePoint);
  };

  const onConfirmIssuePoint = async () => {
    if (!cardApplicationId) {
      dispatch(
        addNotification({
          message: 'Не удалось определить заявление для изменения способа получения',
          type: SnackbarNotificationType.Error,
        }),
      );
      return;
    }

    if (!currentMarker) {
      dispatch(
        addNotification({
          message: 'Выберите пункт выдачи',
          type: SnackbarNotificationType.Error,
        }),
      );
      return;
    }

    try {
      setDeliveryChangeSubmitting(true);
      await dispatch(
        changeCardApplicationDeliveryToIssuePoint({
          cardApplicationId,
          counterpartyDepartmentId: currentMarker.id,
          deliveryTypeId: DeliveryType.IssuePoint,
          desiredAddress: currentMarker.address,
        }),
      );
      dispatch(
        addNotification({
          message: 'Способ получения изменён на пункт выдачи',
          type: SnackbarNotificationType.Success,
        }),
      );
      setIsIssuePointSelectionVisible(false);
      resetDeliveryChangeSelection();
    } catch (e) {
      const error = e as ServerValidationError;
      const errorText = error.data?.[0]?.message || error.message || 'Ошибка. Повторите еще раз';
      dispatch(
        addNotification({
          message: errorText,
          type: SnackbarNotificationType.Error,
        }),
      );
    } finally {
      setDeliveryChangeSubmitting(false);
    }
  };

  const onConfirmRussianPost = async () => {
    if (!cardApplicationId) {
      dispatch(
        addNotification({
          message: 'Не удалось определить заявление для изменения способа получения',
          type: SnackbarNotificationType.Error,
        }),
      );
      return;
    }

    if (!russianPostDeliveryAddressText) {
      dispatch(
        addNotification({
          message: 'Укажите адрес доставки',
          type: SnackbarNotificationType.Error,
        }),
      );
      return;
    }

    if (!isRussianPostDeliveryAddressValid) return;

    try {
      setDeliveryChangeSubmitting(true);
      await dispatch(
        changeCardApplicationDeliveryToRussianPost({
          cardApplicationId,
          deliveryTypeId: DeliveryType.RussianPost,
          desiredAddress: russianPostDeliveryAddressText,
        }),
      );
      dispatch(
        addNotification({
          message: 'Способ получения изменён на Почту России',
          type: SnackbarNotificationType.Success,
        }),
      );
      setIsRussianPostAddressInputVisible(false);
      resetDeliveryChangeSelection();
    } catch (e) {
      const error = e as ServerValidationError;
      const errorText = error.data?.[0]?.message || error.message || 'Ошибка. Повторите еще раз';
      dispatch(
        addNotification({
          message: errorText,
          type: SnackbarNotificationType.Error,
        }),
      );
    } finally {
      setDeliveryChangeSubmitting(false);
    }
  };

  const openDeliveryMethodChange = () => {
    resetDeliveryChangeSelection();

    if (currentDeliveryTypeId === DeliveryType.IssuePoint) {
      setIsRussianPostAddressInputVisible(true);
      return;
    }

    setIsIssuePointSelectionVisible(true);
  };

  const closeDeliveryMethodChange = useCallback(() => {
    setIsIssuePointSelectionVisible(false);
    setIsRussianPostAddressInputVisible(false);
    resetDeliveryChangeSelection();

    if (isOwnAccount) {
      dispatch(getHolderProfile());
    }
  }, [dispatch, isOwnAccount, resetDeliveryChangeSelection]);

  return {
    canChangeDeliveryMethod,
    currentDeliveryTypeId,
    isIssuePointSelectionVisible,
    setIsIssuePointSelectionVisible,
    isRussianPostAddressInputVisible,
    setIsRussianPostAddressInputVisible,
    closeDeliveryMethodChange,
    russianPostDeliveryAddress,
    setRussianPostDeliveryAddress,
    russianPostDeliveryAddressText,
    isRussianPostDeliveryAddressValid,
    isIssuePointDetailsVisible,
    setIsIssuePointDetailsVisible,
    mapLoading,
    mapData,
    currentMarker,
    markers,
    mapCenterPoint,
    mapZoomLevel,
    onIssuePointSelected,
    onNativeIssuePointSelected,
    onConfirmIssuePoint,
    onConfirmRussianPost,
    openDeliveryMethodChange,
    deliveryChangeSubmitting,
    nativeRegion,
    lastMarkerClickTimestamp,
    setLastMarkerClickTimestamp,
    onPressMarker,
    destroyNativeMap,
  };
}
